import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listOrganizationInvitations,
  resendOrganizationInvitation,
  revokeOrganizationInvitation,
} from "../settings/settings-api";
import type { OwnerInvitation } from "../settings/types";
import { inviteLink } from "./access-model";

/**
 * Owner's R3 invitations. Links exist only in create/resend responses, so the ones issued
 * in this page session are kept in memory; a listed invitation has none to copy.
 */
export function useInvitations({ orgId, token, enabled, onChanged }: {
  orgId: string | undefined;
  token: string | null;
  enabled: boolean;
  /** The members list also carries pending invites as rows. */
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["organisation", "invitations", orgId];
  const list = useQuery({
    queryKey,
    queryFn: () => listOrganizationInvitations(orgId as string, token as string),
    enabled: enabled && !!orgId && !!token,
    retry: false,
  });
  const [links, setLinks] = useState<Record<string, string>>({});
  const [lastIssued, setLastIssued] = useState<{ invite: OwnerInvitation; link: string | null } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey });
    onChanged();
  };

  const remember = (invite: OwnerInvitation) => {
    const link = inviteLink(invite);
    if (link) setLinks((prev) => ({ ...prev, [invite.id]: link }));
    setLastIssued({ invite, link });
    refresh();
  };

  const resend = async (invitationId: string) => {
    const invite = await resendOrganizationInvitation(orgId as string, invitationId, token as string);
    remember(invite);
    return invite;
  };

  const revoke = async (invitationId: string) => {
    await revokeOrganizationInvitation(orgId as string, invitationId, token as string);
    setLinks(({ [invitationId]: _dead, ...rest }) => rest);
    setLastIssued((prev) => (prev?.invite.id === invitationId ? null : prev));
    refresh();
  };

  const resendPendingFor = async (email: string) => {
    const pending = (list.data ?? []).find(
      (i) => i.status === "pending" && i.email.toLowerCase() === email.trim().toLowerCase(),
    );
    if (!pending) return false;
    await resend(pending.id);
    return true;
  };

  return {
    invitations: list.data ?? [],
    loading: list.isLoading,
    links,
    lastIssued,
    dismissIssued: () => setLastIssued(null),
    remember,
    resend,
    revoke,
    resendPendingFor,
  };
}

export type InvitationsState = ReturnType<typeof useInvitations>;
